<script setup lang="ts">
import { getPagesList } from '~/layers/cms/app/composables/cms'
import InputText from 'primevue/inputtext'
import { useToast as usePrimeToast } from 'primevue/usetoast'
import { inject, onMounted, ref } from 'vue'
import type { page } from '~/layers/cms/app/types/cmsTypes'

import { FilterMatchMode } from '@primevue/core/api'
import { usePath } from '~/layers/cms/app/composables/usePath'

const toast = usePrimeToast()

interface DialogRef {
  value: {
    data: {
      choose: boolean
    }
    close(payload: { pageURL: string }): void
  }
}
const dialogRef = inject<DialogRef>('dialogRef')

const closeDialog = (pageURL: string) => {
  if (!dialogRef) return
  dialogRef.value.close({ pageURL })
}

const pages = ref<page[]>([])
const filters = ref({
  group: { value: null, matchMode: FilterMatchMode.CONTAINS }, // bedą w przyszłości
  slug: { value: null, matchMode: FilterMatchMode.CONTAINS },
  isCustomPage: { value: null, matchMode: FilterMatchMode.EQUALS }
})

const refreshPagesList = async () => {
  const response = await getPagesList()

  if (!response.status_ok) {
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Nie udało się pobrac listy stron',
      life: 3000
    })
    return
  }

  pages.value = response.data as page[]
}
const currentPath = usePath()
onMounted(refreshPagesList)
</script>

<template>
  <div>
    <div
      v-if="pages?.length"
      class="flex flex-col gap-4"
    >
      <DataTable
        v-model:filters="filters"
        :value="pages"
        scrollable
      >
        <template #header>
          <div class="flex justify-start">
            <IconField>
              <InputIcon>
                <i class="pi pi-search" />
              </InputIcon>
              <InputText
                v-model="filters['slug'].value"
                placeholder="Wyszukaj po slug"
              />
            </IconField>
          </div>
        </template>
        <Column
          field="slug"
          header="Strona"
          sortable
        >
          <template #body="{ data }">
            <div class="flex items-center gap-3">
              <div
                class="font-medium"
              >
                {{ data.metaTitle }} ({{ data.slug }})
              </div>
              <Chip
                v-if="currentPath === data.slug"
                label="Obecna"
                icon="pi pi-check"
                class="text-xs"
              />
            </div>
          </template>
        </Column>
        <Column
          field="isCustomPage"
          header="Typ"
          sortable
        >
          <template #body="{ data }">
            <span
              class="inline-flex text-xs font-medium"
              :class="data.isCustomPage
                ? ' text-blue-700'
                : 'text-orange-600'"
            >
              {{ data.isCustomPage ? 'Własna' : 'Wbudowana' }}
            </span>
          </template>
        </Column>
        <Column
          field="group"
          header="Grupa"
          sortable
        />
        <Column class="w-24">
          <template #body="{ data }">
            <div class="flex flex-row gap-4 justify-end">
              <Button
                type="button"
                label="Wybierz"
                severity="secondary"
                @click="closeDialog(data.slug)"
              />
            </div>
          </template>
        </Column>
      </DataTable>
    </div>
    <div v-else>
      Nie załadowano stron
    </div>
  </div>
</template>
